import { Switch, Route, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './lib/LanguageContext';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import ScrollTracker from "@/components/ScrollTracker";
import PageTransition from "@/components/PageTransition";
import Home from "@/pages/Home";
import Destinations from "@/pages/Destinations";
import Services from "@/pages/Services";
import Booking from "@/pages/Booking";
import About from "@/pages/About";
import NotFound from "@/pages/not-found";
import ScrollToTop from "@/components/ScrollToTop";
import WishlistPage from "@/pages/WishlistPage";
import Accommodation from "@/pages/Accommodation";
import AirTicketing from "@/pages/AirTicketing";
import Blog from "@/pages/Blog";
import ExploreUganda from "@/pages/ExploreUganda";
import Gallery from "@/pages/Gallery";
import TravelInfo from "@/pages/TravelInfo";
import Ecocentric from "@/pages/Ecocentric";
import RedeemOffer from "@/pages/RedeemOffer";
import Admin1 from "@/pages/Admin1";
//import EcoDb from "@/pages/EcoDb";
// Import service pages
import GorillaPrimateTrekking from "@/pages/services/GorillaPrimateTrekking";
import AdventureActivities from "@/pages/services/AdventureActivities";
import CulturalCulinaryTours from "@/pages/services/CulturalCulinaryTours";
import AirportTransport from "@/pages/services/AirportTransport";
import WildlifeBirdingSafaris from "@/pages/services/WildlifeBirdingSafaris";
import CarHireServices from "@/pages/services/CarHireServices";
// Import Uganda tour pages
import MurchisonFalls3Day from "@/pages/tours/uganda/murchison-falls-3-day";
import QueenElizabeth3Day from "@/pages/tours/uganda/queen-elizabeth-3-day";
import BwindiGorilla5Day from "@/pages/tours/uganda/bwindi-gorilla-5-day";
import KidepValley5Day from "@/pages/tours/uganda/kidepo-valley-5-day";
import UgandaWildlife5Day from "@/pages/tours/uganda/wildlife-5-day";
import UgandaAdventure8Day from "@/pages/tours/uganda/adventure-8-day";
import PrimatesWildlife8Day from "@/pages/tours/uganda/primates-wildlife-8-day";
import ExperienceNature19Day from "@/pages/tours/uganda/experience-nature-19-day";
import CulturalWildlife21Day from "@/pages/tours/uganda/cultural-wildlife-21-day";
// Import Rwanda tour pages
import CompleteRwanda7Day from "@/pages/tours/rwanda/complete-7-day";
import DoubleGorilla4Day from "@/pages/tours/rwanda/double-gorilla-4-day";
import NyungweKivu4Day from "@/pages/tours/rwanda/nyungwe-kivu-4-day";
import GorillaMonkey3Day from "@/pages/tours/rwanda/gorilla-monkey-3-day";
import NyungweChimp2Day from "@/pages/tours/rwanda/nyungwe-chimp-2-day";
import Volcanoes7Day from "@/pages/tours/rwanda/volcanoes-7-day";
import RwandaSafari10Day from "@/pages/tours/rwanda/safari-10-day";
import GorillaSafari2Day from "@/pages/tours/rwanda/gorilla-2-day";
import PrimatesSafari5Day from "@/pages/tours/rwanda/primates-5-day";
import Akagera4Day from "@/pages/tours/rwanda/akagera-4-day";
// Import Tanzania tour pages
import TanzaniaSafari2Day from "@/pages/tours/tanzania/safari-2-day";
import TanzaniaSafari6Day from "@/pages/tours/tanzania/safari-6-day";
import TanzaniaWildlife8Day from "@/pages/tours/tanzania/wildlife-8-day";
// Import Kenya tour pages
import MaasaiMara3Day from "@/pages/tours/kenya/maasai-mara-3-day";
import MasaiMaraRoad3Day from "@/pages/tours/kenya/masai-mara-road-3-day";
import MasaiMaraRoad4Day from "@/pages/tours/kenya/masai-mara-road-4-day";
import NakuruMaasaiMara5Day from "@/pages/tours/kenya/nakuru-maasai-mara-5-day";
import KenyaFamily7Day from "@/pages/tours/kenya/family-7-day";
import KenyaMagical9Day from "@/pages/tours/kenya/magical-9-day";


import Loader from "@/components/Loader";
import { useState, useEffect } from "react";

export default function App() {
  const [location] = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [location]);

  // Don't show navbar and footer on admin page
  const isAdminRoute = location === "/admin1" || location === "/ecodb";

  // Define available languages (these should match the ones in Navbar.tsx) 
  const languages = [
    { name: "English", code: "en", flag: "/united-kingdom.png" },
    { name: "French", code: "fr", flag: "/france.png" },
    { name: "Spanish", code: "es", flag: "/spain.png" },
    { name: "German", code: "de", flag: "/german.png" },
    { name: "Italian", code: "it", flag: "/italy.png" },
    { name: "Dutch", code: "nl", flag: "/netherlands.png" },
    { name: "Portuguese", code: "pt", flag: "/portugal.png" },
    { name: "Chinese", code: "zh", flag: "/china.png" },
    { name: "Japanese", code: "ja", flag: "/japan.png" },
    { name: "Arabic", code: "ar", flag: "/arabic.png" },
    { name: "Russian", code: "ru", flag: "/russia.png" },
    { name: "Swahili", code: "sw", flag: "/tanzania.png" },
  ];

  // Prevent right-click on images
  if (typeof window !== 'undefined') {
    document.addEventListener('contextmenu', (e) => {
      if ((e.target as HTMLElement).tagName === 'IMG') {
        e.preventDefault();
      }
    });
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider languages={languages}>
          {isLoading && <Loader />}
          <div className="min-h-screen flex flex-col">
            {!isAdminRoute && <Navbar />}
            <main className={`flex-grow ${isAdminRoute ? 'p-0' : ''}`}>
              <ScrollTracker />
              <ScrollToTop />
              <AnimatePresence mode="wait">
                <Switch location={location} key={location}>
                  <Route path="/">
                    <PageTransition>
                      <Home />
                    </PageTransition>
                  </Route>
                  <Route path="/explore-uganda">
                    <PageTransition>
                      <ExploreUganda />
                    </PageTransition>
                  </Route>
                  <Route path="/destinations">
                    <PageTransition>
                      <Destinations />
                    </PageTransition>
                  </Route>
                  <Route path="/services">
                    <PageTransition>
                      <Services />
                    </PageTransition>
                  </Route>
                  <Route path="/booking">
                    <PageTransition>
                      <Booking />
                    </PageTransition>
                  </Route>
                  <Route path="/about">
                    <PageTransition>
                      <About />
                    </PageTransition>
                  </Route>
                  <Route path="/wishlist">
                    <PageTransition>
                      <WishlistPage />
                    </PageTransition>
                  </Route>
                  <Route path="/services/accommodation">
                    <PageTransition>
                      <Accommodation />
                    </PageTransition>
                  </Route>
                  <Route path="/services/air-ticketing">
                    <PageTransition>
                      <AirTicketing />
                    </PageTransition>
                  </Route>
                  <Route path="/blog">
                    <PageTransition>
                      <Blog />
                    </PageTransition>
                  </Route>
                  <Route path="/gallery">
                    <PageTransition>
                      <Gallery />
                    </PageTransition>
                  </Route>
                  <Route path="/travel-info">
                    <PageTransition>
                      <TravelInfo />
                    </PageTransition>
                  </Route>
                  <Route path="/ecocentric">
                    <PageTransition>
                      <Ecocentric />
                    </PageTransition>
                  </Route>
                  <Route path="/redeem-offer">
                    <PageTransition>
                      <RedeemOffer />
                    </PageTransition>
                  </Route>
                  <Route path="/admin1">
                    <Admin1 />
                  </Route>
                  {/* <Route path="/ecodb">
                    <EcoDb />
                  </Route> */}

                  {/* Uganda Tour Routes */}
                  <Route path="/tours/uganda/murchison-falls-3-day">
                    <PageTransition>
                      <MurchisonFalls3Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/queen-elizabeth-3-day">
                    <PageTransition>
                      <QueenElizabeth3Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/bwindi-gorilla-5-day">
                    <PageTransition>
                      <BwindiGorilla5Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/kidepo-valley-5-day">
                    <PageTransition>
                      <KidepValley5Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/wildlife-5-day">
                    <PageTransition>
                      <UgandaWildlife5Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/adventure-8-day">
                    <PageTransition>
                      <UgandaAdventure8Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/primates-wildlife-8-day">
                    <PageTransition>
                      <PrimatesWildlife8Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/experience-nature-19-day">
                    <PageTransition>
                      <ExperienceNature19Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/uganda/cultural-wildlife-21-day">
                    <PageTransition>
                      <CulturalWildlife21Day />
                    </PageTransition>
                  </Route>

                  {/* Rwanda Tour Routes */}
                  <Route path="/tours/rwanda/complete-7-day">
                    <PageTransition>
                      <CompleteRwanda7Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/double-gorilla-4-day">
                    <PageTransition>
                      <DoubleGorilla4Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/nyungwe-kivu-4-day">
                    <PageTransition>
                      <NyungweKivu4Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/gorilla-monkey-3-day">
                    <PageTransition>
                      <GorillaMonkey3Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/nyungwe-chimp-2-day">
                    <PageTransition>
                      <NyungweChimp2Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/volcanoes-7-day">
                    <PageTransition>
                      <Volcanoes7Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/safari-10-day">
                    <PageTransition>
                      <RwandaSafari10Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/gorilla-2-day">
                    <PageTransition>
                      <GorillaSafari2Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/primates-5-day">
                    <PageTransition>
                      <PrimatesSafari5Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/rwanda/akagera-4-day">
                    <PageTransition>
                      <Akagera4Day />
                    </PageTransition>
                  </Route>

                  {/* Tanzania Tour Routes */}
                  <Route path="/tours/tanzania/safari-2-day">
                    <PageTransition>
                      <TanzaniaSafari2Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/tanzania/safari-6-day">
                    <PageTransition>
                      <TanzaniaSafari6Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/tanzania/wildlife-8-day">
                    <PageTransition>
                      <TanzaniaWildlife8Day />
                    </PageTransition>
                  </Route>

                  {/* Kenya Tour Routes */}
                  <Route path="/tours/kenya/maasai-mara-3-day">
                    <PageTransition>
                      <MaasaiMara3Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/kenya/masai-mara-road-3-day">
                    <PageTransition>
                      <MasaiMaraRoad3Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/kenya/masai-mara-road-4-day">
                    <PageTransition>
                      <MasaiMaraRoad4Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/kenya/nakuru-maasai-mara-5-day">
                    <PageTransition>
                      <NakuruMaasaiMara5Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/kenya/family-7-day">
                    <PageTransition>
                      <KenyaFamily7Day />
                    </PageTransition>
                  </Route>
                  <Route path="/tours/kenya/magical-9-day">
                    <PageTransition>
                      <KenyaMagical9Day />
                    </PageTransition>
                  </Route>

                  {/* Service Routes */}
                  <Route path="/services/gorilla-primate-trekking">
                    <PageTransition>
                      <GorillaPrimateTrekking />
                    </PageTransition>
                  </Route>
                  <Route path="/services/soft-extreme-adventures">
                    <PageTransition>
                      <AdventureActivities />
                    </PageTransition>
                  </Route>
                  <Route path="/services/cultural-culinary-tours">
                    <PageTransition>
                      <CulturalCulinaryTours />
                    </PageTransition>
                  </Route>
                  <Route path="/services/airport-transport">
                    <PageTransition>
                      <AirportTransport />
                    </PageTransition>
                  </Route>
                  <Route path="/services/wildlife-birding-safaris">
                    <PageTransition>
                      <WildlifeBirdingSafaris />
                    </PageTransition>
                  </Route>
                  <Route path="/services/car-hire">
                    <PageTransition>
                      <CarHireServices />
                    </PageTransition>
                  </Route>
                  <Route>
                    <PageTransition>
                      <NotFound />
                    </PageTransition>
                  </Route>
                </Switch>
              </AnimatePresence>
            </main>
            {!isAdminRoute && <Footer />}
            {/* Show the WhatsApp bubble on all non-admin pages */}
            {!isAdminRoute && <WhatsAppBubble />}
          </div>
          <Toaster />
        </LanguageProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}